import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { creatorbase, creatorbaseQueries, getPaginatedIdQueryParam, youtube, youtubeQueries } from '@round/api';
import { useYoutubeChannelsListQueries } from '@round/platforms/youtube';
import { useInfinitePosts, usePostsTotals } from '@round/creatorbase-helpers/api-hooks';
import { isNumber } from 'utility/utility';
import uniq from 'lodash/uniq';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';

export type YoutubeReportPostsTableRow = creatorbase.YoutubePost & {
    channel: youtube.Channel | null;
};

// Serves as both the posts page size and the channel-id batch size, so one posts page never needs
// more than one batch of channel requests.
const BATCH_SIZE = 50;

// One request per batch of channel ids, with page_size following the batch so no batch is
// truncated. The declarative reads and the imperative ensureQueryData calls build their params
// here, so equal id sets produce identical query keys and share cache entries.
const buildChannelParams = (channelIds: number[]) => ({ id: channelIds.join(','), page_size: channelIds.length });

// Every page issues a query — disabled when the page has no channels — so a query's index is its
// page index.
const collectLoadingPageIndexes = (results: { isLoading: boolean }[]) =>
    new Set(results.flatMap((result, index) => (result.isLoading ? [index] : [])));

const getYoutubeChannelIds = (posts: creatorbase.YoutubePost[]) =>
    uniq(posts.map((post) => post.youtube_details?.account_id).filter(isNumber));

const assembleRows = (
    posts: creatorbase.YoutubePost[],
    channelsById: Map<number, youtube.Channel>
): YoutubeReportPostsTableRow[] =>
    posts.map((post) => {
        const accountId = post.youtube_details?.account_id;
        return {
            ...post,
            channel: isNumber(accountId) ? channelsById.get(accountId) ?? null : null,
        };
    });

export const getReportYoutubePostsParams = (campaignId: number): creatorbase.GetPostsParams => ({
    campaign_id: campaignId.toString(),
    ordering: '-view_count',
    exclude_statuses: backupPostsStatuses.join(','),
});

// Totals must cover exactly the list's filtered set, so they're derived from the list params
// rather than built independently.
const getReportYoutubePostsTotalsParams = (campaignId: number) => {
    const { ordering, ...params } = getReportYoutubePostsParams(campaignId);
    return params;
};

export function useYoutubeReportCampaignPosts(campaignId: number) {
    const infinitePostsQuery = useInfinitePosts({ ...getReportYoutubePostsParams(campaignId), page_size: BATCH_SIZE });

    // Rows are partial until every page is loaded, so the footers always read API totals rather than
    // reducing over the rendered rows. isLoading, not isFetching: a background refetch would
    // otherwise skeleton every footer while the row cells keep their values.
    const { data: postsTotals, isLoading: areTotalsLoading } = usePostsTotals(
        getReportYoutubePostsTotalsParams(campaignId)
    );

    const postPages = (infinitePostsQuery.data?.pages ?? []).map((page) => page.results as creatorbase.YoutubePost[]);
    const posts = postPages.flat();

    // Channel ids are batched by posts page, so loading another page never changes an earlier page's
    // query key.
    const channelIdBatches = postPages.map(getYoutubeChannelIds);

    const channelsData = useYoutubeChannelsListQueries(
        channelIdBatches.map((ids) => ({
            params: buildChannelParams(ids),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(
                    results.flatMap((result) => result.data?.results ?? []).map((channel) => [channel.id, channel])
                ),
                loadingPages: collectLoadingPageIndexes(results),
            }),
        }
    );

    const pageIndexByPostId = new Map(
        postPages.flatMap((pagePosts, pageIndex) => pagePosts.map((post) => [post.id, pageIndex] as const))
    );

    // A row's loading state is its own page's, so a later page's fetch never skeletons earlier rows.
    const getIsAccountDataLoading = (post: YoutubeReportPostsTableRow) => {
        if (!isNumber(post.youtube_details?.account_id)) {
            return false;
        }

        const pageIndex = pageIndexByPostId.get(post.id);
        if (pageIndex === undefined) {
            return false;
        }

        return channelsData.loadingPages.has(pageIndex);
    };

    return {
        data: assembleRows(posts, channelsData.byId),
        getIsAccountDataLoading,
        hasError: infinitePostsQuery.isError,
        isLoading: infinitePostsQuery.isLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage: infinitePostsQuery.hasNextPage,
        isFetchingNextPage: infinitePostsQuery.isFetchingNextPage,
        fetchNextPage: infinitePostsQuery.fetchNextPage,
    };
}

// Imperative twin: read-or-fetch enriched posts from the cache.
// Use from event handlers (e.g. copy-to-clipboard) where a live subscription
// isn't wanted. Shares keys with the hook, so an already-rendered table is a
// pure cache hit.
export function useEnsureYoutubeReportCampaignPosts() {
    const queryClient = useQueryClient();

    return useCallback(
        async (campaignId: number): Promise<YoutubeReportPostsTableRow[]> => {
            const posts = (await queryClient.ensureQueryData(
                creatorbaseQueries.posts.options.listAll(getReportYoutubePostsParams(campaignId))
            )) as creatorbase.YoutubePost[];

            const channels = await Promise.all(
                getPaginatedIdQueryParam(getYoutubeChannelIds(posts), BATCH_SIZE).map((ids) =>
                    queryClient.ensureQueryData(youtubeQueries.channels.options.list(buildChannelParams(ids)))
                )
            );

            const channelsById = new Map(
                channels.flatMap((result) => result.results).map((channel) => [channel.id, channel])
            );

            return assembleRows(posts, channelsById);
        },
        [queryClient]
    );
}
